import { Link } from 'react-router-dom';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useAccountUsage } from '../../hooks/useAccountUsage';
import {
  SPENDING_PAGE_PATH,
  SPENDING_TEXT_CLASSES,
  formatBilledCents,
  formatSpendingCentsShort,
  spendingReading,
} from '../../utils/spending';

/**
 * UsageIndicator shows the current period's endpoint call count against the
 * plan's included_endpoint_calls cap. Only renders on accounts where a finite
 * cap exists (i.e. the claim is present and is not -1).
 *
 * Exception-based: below the near-cap threshold (80% of the cap) it renders
 * nothing — usage is not permanent bar furniture. It appears amber when near
 * the cap (>= 80%) and red at/over the cap. Day-to-day usage is reachable via
 * UserMenu → Plan & Billing instead.
 *
 * When the billing page flag is on, the indicator links to /settings/billing.
 *
 * The current count is fetched from GET /api/accounts/usage (innovate-service,
 * task 076 metering). If that endpoint is not yet deployed (404) the indicator
 * shows nothing rather than an error — the feature degrades gracefully.
 *
 * The cap comes from the same response (task 676 — previously the JWT `limits`
 * claim), so the count and the cap it is measured against are always read from
 * one consistent snapshot.
 *
 * Second trigger (task 1159): the account's spending limit. The indicator also
 * appears once a spending threshold alert has been sent this month
 * (`spending.alertsSent.length > 0`) or while the hard stop is refusing calls
 * (`spending.stopped`): amber "$160 / $200 usage" while calls continue, red
 * "$200 limit · calls stopped" while they are refused. It links to
 * Settings › Billing › Spending when that page is open to the User, else to
 * Billing. Both triggers can show at once; each reads its own figure.
 */
export default function UsageIndicator() {
  const billingEnabled = useFlag('innovate.fe.billing.page');
  const spendingPageEnabled = useFlag('innovate.fe.spending.page');

  // The cap now arrives with the count, so the request can no longer be gated
  // on already knowing the cap — it is what the request answers.
  const { data, isLoading } = useAccountUsage();

  if (isLoading || !data) return null;

  const callsIndicator = includedCallsIndicator(data.endpointCallCount, data.limits?.['included_endpoint_calls']);
  const spending = spendingReading(data.spending);
  const spendingShown = spending.hasLimit && (spending.stopped || spending.crossed);

  if (!callsIndicator && !spendingShown) return null;

  const wrap = (node: React.ReactNode, to: string, title: string, testId: string) =>
    billingEnabled || (to === SPENDING_PAGE_PATH && spendingPageEnabled) ? (
      <Link to={to} className="inline-flex hover:opacity-80 transition-opacity" title={title} data-testid={testId}>
        {node}
      </Link>
    ) : (
      node
    );

  const spendingNode = spendingShown ? (
    <span
      data-testid="usage-indicator-spending"
      data-tone={spending.tone}
      className={`text-xs font-medium ${SPENDING_TEXT_CLASSES[spending.tone]}`}
      title={`Usage cost this month: ${formatBilledCents(spending.billedCents, true)} of the ${formatSpendingCentsShort(spending.limitCents as number)} spending limit`}
    >
      {spending.stopped
        ? `${formatSpendingCentsShort(spending.limitCents as number)} limit · calls stopped`
        : `${formatBilledCents(spending.billedCents, true)} / ${formatSpendingCentsShort(spending.limitCents as number)} usage`}
    </span>
  ) : null;

  return (
    <>
      {callsIndicator && wrap(callsIndicator, '/settings/billing', 'Plan & Billing', 'usage-indicator-link')}
      {spendingNode &&
        wrap(
          spendingNode,
          spendingPageEnabled ? SPENDING_PAGE_PATH : '/settings/billing',
          'Spending',
          'usage-indicator-spending-link'
        )}
    </>
  );
}

/** The included-calls trigger: amber from 80% of a finite cap, red at or over it. */
function includedCallsIndicator(current: number, cap: number | undefined) {
  // Only show the indicator when there is a finite cap.
  const hasCap = cap !== undefined && cap !== -1 && cap > 0;
  if (!hasCap) return null;

  const atOrOverCap = current >= cap;
  // Amber emphasis as the cap approaches (>= 80%) so the wall isn't a surprise.
  const nearCap = !atOrOverCap && current / cap >= 0.8;

  // Exception-based rendering: below the near-cap threshold, show nothing.
  if (!atOrOverCap && !nearCap) return null;

  return (
    <span
      data-testid="usage-indicator"
      className={`text-xs font-medium ${
        atOrOverCap
          ? 'text-red-600 dark:text-red-400'
          : 'text-amber-600 dark:text-amber-400'
      }`}
      title={`Endpoint calls this period: ${current} of ${cap} included`}
    >
      {current}&thinsp;/&thinsp;{cap} calls
    </span>
  );
}
